import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner, Button, Form, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import ReactDatePicker from 'react-datepicker';
import { datePickerValue, formatDate, parseUserInfo, smartSum, transErrorMessage } from '../../utilities/common';
import Select from 'react-select';
import DataTable from '../../components/DataTable';
import { AiOutlineEdit } from 'react-icons/ai';
import { IoTrashOutline } from 'react-icons/io5';
import { IoCloseOutline } from 'react-icons/io5';
import { IoSaveOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { fetchCustomEmissionFactors } from './emissionProfileSlice';
import { getReportUnitOptions } from '../reports/reportHelper';
import AddCustomFactor from './form/AddCustomFactor';
import { createCustomEmissionFactorsApi, removeCustomEmissionFactorsApi, updateCustomEmissionFactorsApi } from './emissionProfileApi';
import _ from 'lodash';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import LoadingButton from '../../components/LoadingButton';
import { Controller, useWatch } from 'react-hook-form';
import CustomEmissionSource from './form/CustomEmissionSource';
import CircleTooltip from '../../components/CircleTooltip';
import { getActivityTypeOptions } from '../model-scenarios/scenarioConfig';

const ManageCustomFactor = ({ epsId, control, setValue, register, errors, isEditing }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [localFactors, setLocalFactors] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const customEmissionFactors = useSelector((state) => state.emissionProfiles.customEmissionFactors);
  const customEmissionEpsId = useSelector((state) => state.emissionProfiles.customEmissionEpsId);
  const orgReportUnits = useSelector((state) => state.organizations.reportUnits);
  const { orgId, usrDateFormat, usrOrgOptions } = parseUserInfo(useSelector((state) => state.users.user));

  const custom = useWatch({
    control,
    name: 'custom'
  });

  useEffect(() => {
    if (customEmissionFactors && localFactors === null && customEmissionEpsId === epsId) {
      setLocalFactors(customEmissionFactors);
    }
  }, [customEmissionFactors, localFactors, customEmissionEpsId, epsId]);

  const unitOptions = getReportUnitOptions(t, orgReportUnits);
  const getSelectedOption = useCallback((target, options, isShowLabel = false) => {
    let selected = { value: target, label: target };
    options.forEach((item) => {
      item.options.forEach((opt) => {
        if (target === opt.value) {
          selected = !isShowLabel ? { ...opt, label: opt.value } : opt;
        }
      });
    });
    return selected;
  }, []);

  const onDeleteConfirmHandler = (factor) => {
    setDeletingItem(factor);
  };

  const activityOptions = getActivityTypeOptions(t);

  const addCustomEmissionFactor = async (formData) => {
    try {
      setIsLoading(true);
      const newItem = await createCustomEmissionFactorsApi(orgId, { ...formData, epsId });
      setLocalFactors(prev => [...prev, newItem]);
      dispatch(fetchCustomEmissionFactors({ orgId, epsId }));
      toast.success(t('The custom emission factor saved successfully.'));
    } catch (e) {
      toast.error(transErrorMessage(t, e));
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const removeCustomEmissionFactor = async () => {
    try {
      setIsLoading(true);
      await removeCustomEmissionFactorsApi(deletingItem.id, orgId);
      setLocalFactors(prev => prev.filter(f => f.id !== deletingItem.id));
      dispatch(fetchCustomEmissionFactors({ orgId, epsId }));
      toast.success(t('The custom emission factor has been deleted.'));
    } catch (e) {
      toast.error(transErrorMessage(t, e));
    } finally {
      setIsLoading(false);
      setDeletingItem(null);
    }
  };

  const updateCustomEmissionFactor = async (edited, original) => {
    if (_.isEqual({ id: original.id, ...edited }, original)) {
      return;
    }
    try {
      setUpdatingId(original.id);
      setIsLoading(true);
      await updateCustomEmissionFactorsApi(original.id, orgId, edited);
      setLocalFactors(prev =>
        prev.map(f => f.id === original.id ? { ...f, ...edited } : f)
      );
      dispatch(fetchCustomEmissionFactors({ orgId, epsId }));
      toast.success(t('The custom emission factor has been updated.'));
    } catch (e) {
      toast.error(transErrorMessage(t, e));
      throw e;
    } finally {
      setUpdatingId(null);
      setIsLoading(false);
    }
  };
  const gasFields = useMemo(() => ['co2Factor', 'ch4Factor', 'n2oFactor'], []);
  const requireFields = useMemo(() => ['co2eFactor', 'fromDate', 'toDate', 'unit'], []);

  const isInvalid = useCallback((field, value) => {
    if (field === 'activityType') return false;
    const required = requireFields.includes(field);
    if (['fromDate', 'toDate', 'unit'].includes(field)) {
      return required && !value;
    }
    if (!value && required) {
      return true;
    }
    if (value && (isNaN(parseFloat(value)) || !isFinite(value))) {
      return true;
    }
    return false;
  }, [requireFields]);

  const columns = useMemo(
    () => {
      const renderNumberCol = (title, field) => ({
        Header: `${title}${requireFields.includes(field) ? ' *' : ''}`,
        accessor: field,
        editable: true,
        EditCell: ({ cell, onCellValueChange, row }) => {
          const { updatedValue } = cell.state;
          const handleChange = (e) => {
            if (field === 'co2eFactor') {
              row.cells.forEach((targetCell) => {
                if (gasFields.includes(targetCell.column.id)) {
                  targetCell.setState({ updatedValue: '' });
                }
              });
            } else if (gasFields.includes(field)) {
              const numbers = row.cells.map((targetCell) => {
                if (gasFields.includes(targetCell.column.id)) {
                  const val = field === targetCell.column.id ? e.target.value : targetCell?.state?.updatedValue;
                  if (!val) {
                    return '';
                  }
                  return val && Number(val) ? Number(val) : 0;
                }
                return '';
              });
              const co2eCell = row.cells.find(c => c.column.id === 'co2eFactor');
              const newVal = numbers.every(i => i === '') ? '' : String(smartSum(numbers.filter(i => i !== '')));
              co2eCell.setState({ updatedValue: newVal });
            }
            onCellValueChange(e.target.value);
          };
          const ecUnit = row.cells.find(c => c.column.id === 'unit')?.state?.updatedValue ?? '';
          const displayTitle = field === 'energyContentFactor' ? t('Energy Content Factor (GJ/{{unit}})', { unit: ecUnit ?? '' }) : title;
          return (
            <Form.Group className="mb-0">
              <Form.Control
                value={updatedValue}
                type="text"
                onChange={handleChange}
                isInvalid={isInvalid(field, updatedValue)}
              />
              <Form.Control.Feedback type="invalid">
                {t("{{title}} must be valid number.", { title: displayTitle })}
              </Form.Control.Feedback>
            </Form.Group>
          )
        }
      });
      const cols = [
        {
          Header: `${t('From')} *`,
          id: 'fromDate',
          accessor: 'fromDate',
          editable: true,
          Cell: ({ row }) => formatDate(row.original?.fromDate),
          EditCell: ({ cell, onCellValueChange }) => {
            const { updatedValue } = cell.state;
            const isInvalidDate = isInvalid('fromDate', updatedValue);
            return (
              <>
                <ReactDatePicker
                  isClearable
                  dateFormat={usrDateFormat}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  className={`form-control ${isInvalidDate ? 'is-invalid' : ''}`}
                  {...datePickerValue(updatedValue, onCellValueChange)}
                />
                {isInvalidDate && (
                  <div className="invalid-feedback d-block">
                    {t('The from date is required')}
                  </div>
                )}
              </>
            );
          }
        },
        {
          Header: `${t('To')} *`,
          accessor: 'toDate',
          editable: true,
          Cell: ({ row }) => formatDate(row.original?.toDate),
          EditCell: ({ cell, onCellValueChange }) => {
            const { updatedValue } = cell.state;
            const isInvalidDate = isInvalid('toDate', updatedValue);
            return (
              <>
                <ReactDatePicker
                  isClearable
                  dateFormat={usrDateFormat}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  className={`form-control ${isInvalidDate ? 'is-invalid' : ''}`}
                  {...datePickerValue(updatedValue, onCellValueChange)}
                />
                {isInvalidDate && (
                  <div className="invalid-feedback d-block">
                    {t('The to date is required')}
                  </div>
                )}
              </>
            );
          }
        },
        {
          Header: `${t('Activity Type')}`,
          id: 'activityType',
          accessor: 'activityType',
          editable: true,
          Cell: ({ row }) => {
            const selected = getSelectedOption(row.original.activityType, activityOptions, true);
            return selected.label;
          },
          EditCell: ({ cell, onCellValueChange }) => {
            const { updatedValue } = cell.state;
            const handleChange = (v) => onCellValueChange(v?.value ?? null);
            return (
              <Select
                value={updatedValue ? getSelectedOption(updatedValue, activityOptions, true) : null}
                options={activityOptions}
                onChange={handleChange}
                placeholder={t('Select Activity Type')}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            );
          }
        },
        {
          Header: `${t('Consumption Unit')} *`,
          id: 'unit',
          accessor: 'unit',
          editable: true,
          EditCell: ({ cell, onCellValueChange }) => {
            const { updatedValue } = cell.state;
            const handleChange = (v) => onCellValueChange(v.value);
            return (
              <Select
                isDisabled
                value={updatedValue ? getSelectedOption(updatedValue, unitOptions) : null}
                options={unitOptions}
                onChange={handleChange}
                placeholder={t('Select Unit')}
              />
            );
          }
        },
        renderNumberCol(t('Emission Factor in CO2-e (t)'), 'co2eFactor'),
        renderNumberCol(t('Energy Content Factor (GJ)/unit'), 'energyContentFactor'),
        renderNumberCol(t('CO2 Conversion Factor'), 'co2Factor'),
        renderNumberCol(t('CH4 Conversion Factor'), 'ch4Factor'),
        renderNumberCol(t('N2O Conversion Factor'), 'n2oFactor'),
        renderNumberCol(t('HFC Conversion Factor'), 'hfcFactor'),
        renderNumberCol(t('SF6 Conversion Factor'), 'sf6Factor'),
        renderNumberCol(t('PFC Conversion Factor'), 'pfcFactor'),
        renderNumberCol(t('NF3 Conversion Factor'), 'nf3Factor'),
        {
          Header: '',
          accessor: 'actions',
          actionColumn: true,
          disableGlobalFilter: false,
          disableSortBy: true,
          EditCell: ({ cell, cancelEditRow, saveRow }) => {
            const isInvalidForm = !!cell?.row?.cells?.find(item => isInvalid(item?.column?.id, item?.state?.updatedValue))
            return (
              <div className="action-buttons">
                <LoadingButton isLoading={isLoading && updatingId === cell?.row?.original?.id} />
                {(!isLoading || updatingId !== cell?.row?.original?.id) &&
                  (
                    <Button
                      disabled={isInvalidForm || isLoading}
                      size="sm"
                      className="mr-1"
                      variant="outline-secondary"
                      onClick={saveRow}
                    >
                      <IoSaveOutline />
                    </Button>
                  )
                }
                <Button size="sm" variant="outline-secondary" onClick={cancelEditRow}>
                  <IoCloseOutline />
                </Button>
              </div>
            );
          },
          Cell: (props) => {
            const { deleteRow, editRow } = props;
            return (
              <div className="d-flex action-buttons">
                <Button size="sm" className="mr-1" variant="outline-secondary" onClick={editRow}>
                  <AiOutlineEdit />
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={deleteRow}>
                  <IoTrashOutline />
                </Button>
              </div>
            );
          }
        }
      ];
      return cols;
    },
    [unitOptions, gasFields, isInvalid, getSelectedOption, t, usrDateFormat, isLoading, updatingId, requireFields, activityOptions]
  );

  useEffect(() => {
    if (custom === 'Y' && usrOrgOptions?.optEnabledCustomFactor) {
      dispatch(fetchCustomEmissionFactors({ orgId, epsId }));
    }
  }, [orgId, usrOrgOptions?.optEnabledCustomFactor, dispatch, epsId, custom]);

  if (!usrOrgOptions?.optEnabledCustomFactor) {
    return (
      <CustomEmissionSource
        control={control}
        setValue={setValue}
        register={register}
        errors={errors}
        isEditing={isEditing}
      />
    );
  }

  return (
    <div className="manage-factor-table">
      <Row>
        <Col>
          <Form.Group className="custom-form-control">
            <Controller
              name="custom"
              control={control}
              render={({ field }) => {
                const parsed = field.value === 'Y' ? true : false;
                const format = () => {
                  field.onChange(field.value === 'Y' ? 'N' : 'Y');
                  setValue('gas', 'N', { shouldValidate: true });
                  setValue('eprflsrcEsrcId', null, { shouldValidate: true });
                  setValue('eprflsrcConversionFactortj', '1', { shouldValidate: true });
                  setValue('eprflsrcUncertainty', '1', { shouldValidate: true });
                  setValue('eprflsrcConversionFactor', '1', { shouldValidate: true });
                  setValue('eprflsrcConsumptionUnit', 'CUSTOM_FACTOR', { shouldValidate: true });
                  setValue('showGeneralEmissionFromEps', false);
                };
                return (
                  <Form.Check>
                    <Form.Check.Input
                      disabled={isEditing}
                      custom
                      id="custom"
                      onChange={format}
                      checked={parsed}
                    />
                    <Form.Check.Label>
                      {t('Custom Emission Factor')}
                      <CircleTooltip
                        className="wide-tooltip"
                        title={t(`Each unit family requires distinct time periods. Overlaps are allowed only between different unit families. For example: 'kg' (from 01/01/2030 to 30/06/2030) and 'g' (from 01/04/2030 to 30/09/2030) cannot exist simultaneously, as their periods overlap within the same unit family (Weight).`)}
                      />
                    </Form.Check.Label>
                  </Form.Check>
                );
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      {custom === 'Y' && localFactors === null && (
        <Spinner animation="grow" />
      )}
      {custom === 'Y' && localFactors !== null && (
        <>
          <DataTable
            columns={columns}
            data={localFactors || []}
            onEditSubmit={updateCustomEmissionFactor}
            onDeleteRow={onDeleteConfirmHandler}
            pageLimit={5}
            controlledPageIndex={0}
            defaultSort="fromDate"
            autoResetRowState={false}
            autoResetSortBy={false}
            footer={
              <AddCustomFactor
                onSubmit={addCustomEmissionFactor}
                isLoading={isLoading}
                unitOptions={unitOptions}
                usrDateFormat={usrDateFormat}
                getSelectedOption={getSelectedOption}
                requireFields={requireFields}
                gasFields={gasFields}
              />
            }
          />
        </>
      )}
      <DeleteConfirmDialog
        visible={deletingItem !== null}
        headerTitle={t('Delete Emission Factor')}
        onDiscardHandler={() => setDeletingItem(null)}
        onDeleteHandler={removeCustomEmissionFactor}
        isLoading={isLoading}
      >
        <p>
          <Trans
            i18nKey="Do you really want to delete the factor date range from <bold>{{fromDate}}</bold> to <bold>{{toDate}}</bold>?"
            values={{
              fromDate: formatDate(deletingItem?.fromDate),
              toDate: formatDate(deletingItem?.toDate),
            }}
            components={{ bold: <strong /> }}
          />
        </p>
      </DeleteConfirmDialog>
    </div>
  );
};

export default ManageCustomFactor;
